import { type FC } from "react";
import { makeStyles } from "tss-react/mui";
import {
  AutocompleteOption,
  MiAutocomplete,
  MiButton,
  MiDatePicker,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { getDate } from "@miplatform/utils";
import { Box } from "@mui/material";
import type { CalendarFilterGroupController } from "./useCalendarFilterGroup";

export const CalendarFilterGroup: FC<CalendarFilterGroupProps> = ({
  filters,
  builderOptions,
  communityOptions,
  statusOptions,
  hasPendingFilters,
  onApplyFilters,
  onResetFilters,
  onToggleFilters,
  handleFilterChange,
  layout = "inline",
}) => {
  const { classes, cx } = useStyles();

  const isInline = layout === "inline";
  const fieldClassName = isInline ? classes.inlineField : classes.stackedField;

  const fields = (
    <>
      <Box className={fieldClassName}>
        <MiAutocomplete
          name="calendar-filter-status"
          label="Status"
          options={statusOptions}
          value={filters.status}
          getOptionLabel={(option: AutocompleteOption) => option.label}
          onChange={(_, value) => handleFilterChange("status", value)}
        />
      </Box>

      <Box className={fieldClassName}>
        <MiAutocomplete
          name="calendar-filter-builder"
          label="Builder"
          options={builderOptions}
          value={filters.builder}
          getOptionLabel={(option: AutocompleteOption) => option.label}
          onChange={(_, value) => handleFilterChange("builder", value)}
        />
      </Box>

      <Box className={fieldClassName}>
        <MiAutocomplete
          name="calendar-filter-community"
          label="Community"
          options={communityOptions}
          value={filters.community}
          getOptionLabel={(option: AutocompleteOption) => option.label}
          onChange={(_, value) => handleFilterChange("community", value)}
        />
      </Box>

      <Box className={fieldClassName}>
        <MiDatePicker
          label="Due date"
          value={filters.dueDate?.toDate() ?? null}
          onChange={(date: Date | null) =>
            handleFilterChange("dueDate", date ? getDate(date) : null)
          }
        />
      </Box>
    </>
  );

  const buttons = (
    <Box className={isInline ? classes.inlineButtons : classes.stackedButtons}>
      <MiButton
        title="Reset filters"
        color={BUTTON_COLORS.BLACK}
        variant={BUTTON_VARIANTS.TEXT}
        disabled={!hasPendingFilters}
        onClick={() => {
          onResetFilters();
          onToggleFilters?.();
        }}
        className={cx(classes.underlined, !isInline && classes.fullWidthButton)}
      />
      <MiButton
        title="Apply filters"
        color={BUTTON_COLORS.PRIMARY}
        variant={BUTTON_VARIANTS.CONTAINED}
        disabled={!hasPendingFilters}
        onClick={onApplyFilters}
        className={cx(!isInline && classes.fullWidthButton)}
      />
    </Box>
  );

  return (
    <Box className={classes.fieldsRow}>
      {fields}
      {buttons}
    </Box>
  );
};

type CalendarFilterGroupProps = Pick<
  CalendarFilterGroupController,
  | "filters"
  | "builderOptions"
  | "communityOptions"
  | "statusOptions"
  | "handleFilterChange"
> & {
  hasPendingFilters: boolean;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  onToggleFilters?: () => void;
  layout?: "inline" | "stacked";
};

const useStyles = makeStyles()((theme) => ({
  fieldsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    alignItems: "flex-end",
  },
  inlineField: {
    display: "flex",
    flex: 1,
    minWidth: 140,
  },
  stackedField: {
    display: "flex",
    flex: 1,
    minWidth: 120,
    backgroundColor: theme.palette.common.white,
    borderRadius: 4,
  },
  inlineButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  stackedButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  fullWidthButton: {
    flex: 1,
  },
  underlined: {
    textDecoration: "underline",
  },
}));
